import axios from 'axios';
import {t} from 'i18next';
import {toast} from 'react-toastify';
import {call, ForkEffect, put, select, takeLatest} from 'redux-saga/effects';
import {IPresentation} from '../../incase-domain/presentation';
import {SlidesAnalyticsData} from '../../incase-domain/proptypes/slidesAnalytics';
import {ISlide} from '../../incase-domain/slide';
import {postSlidesAnalytics} from '../../service/analytics.service';
import {fetchPresentationById} from '../../service/presentation.service';
import {getRunFileStorageKey, getSlugFromUrl} from '../../util/token.utils';
import {PRESENTATION_ENDED_EVENT} from '../../constants';
import {getSymmetricalDifference} from '../../util/array';
import {checkIfBlockedInRoom, checkShouldBlock} from '../../util/blockingRules';
import {convertPresentationToLatestVersion} from '../../util/presentationVersionConverter';
import {socket} from '../../util/socket';
import {fetchSlidesAnalyticsAction, updateSlidesAnalyticsUpdateTimeAction} from '../actions/analytics.actions';
import {clearElementClickedAction, openEndPresentationAction, setElementsClickedAction} from '../actions/chalkBoardElements.actions';
import {
    updateDrawEnabledAction,
    updatePresentationAction,
    updatePresentationHasSlidesAction,
    updatePresentationRunning,
    updatePresentationStatusAction,
} from '../actions/dashboard.actions';
import {setFullscreenAction} from '../actions/layout.actions';
import {closeConfig, closeModalAction} from '../actions/modal.actions';
import {
    advanceSequenceStepAction,
    jumpToSlideAction,
    resetPresentationReducerAction,
    resetSequenceQueueAction,
    setSequenceQueueAction,
    updateCurrentSlide,
    updateLastShowedSlide,
} from '../actions/presentation.actions';
import {updateCurrentOwnerSlideAction} from '../actions/roomRunFile.actions';
import {resetRunfileAction, savePresentationRunFileAction} from '../actions/runfile.actions';
import {END_PRESENTATION, HANDLE_ELEMENTS_CLICKED, IEndPresentationAction} from '../actions/types/chalkBoardElements.actions.types';
import {
    FIND_PRESENTATION_BY_ID,
    GET_PRESENTATION_IN_ROOM,
    IFindPresentationByIdAction,
    IGetPresentationInRoomAction,
    IHandleElementsClicked,
    TOGGLE_DRAW_ENABLED,
} from '../actions/types/dashboard.actions.type';
import {
    IJumpToSlide,
    IJumpToSlideWithId,
    IStartUploadedPresentation,
    JUMP_TO_SLIDE,
    JUMP_TO_SLIDE_WITH_ID,
    SCROLL_LEFT,
    SCROLL_RIGHT,
    START_UPLOADED_PRESENTATION,
} from '../actions/types/presentation.actions.types';
import {SequenceQueueEntry} from '../reducer/presentation.reducer';
import {AppState} from '../reducer';
import {visibleSlideSelector} from '../selectors/presentation.selectors';
import {getRunFile, loadSlidesSaga, refreshPresentationSaga} from './utils/presentationStart.saga.utils';
import {setPasswordErrorAction} from '../actions/roomProtection.actions';

export function* addSlideToRunFile(slideIndex: number): unknown {
    const state: AppState = yield select();
    const {runFile} = state.runFileReducer;
    const {loadedPresentation} = state.presentationReducer;

    const slideId = loadedPresentation?.slides[slideIndex]._id;
    const isAlreadyWatched = slideId && runFile.watchedSlideIds?.includes(slideId);

    const watchedSlideIds =
        !!slideId && runFile.watchedSlideIds && !isAlreadyWatched ? [...runFile.watchedSlideIds, slideId] : runFile.watchedSlideIds;
    const updatedRunFile = {
        ...runFile,
        lastSlideId: slideId,
        watchedSlideIds,
    };

    yield put(savePresentationRunFileAction(updatedRunFile));
}

export function* addTimestampsToSlidesAnalytics(slideIndex: number): unknown {
    const state: AppState = yield select();
    const {slidesAnalytics, lastUpdate} = state.slidesAnalyticsReducer;
    const {loadedPresentation} = state.presentationReducer;
    const slideId = loadedPresentation?.slides[slideIndex]._id;

    if (!slideId) return;

    const time = Date.now();
    const slideData: SlidesAnalyticsData = {
        slidesId: slideId,
        start: lastUpdate,
        end: time,
    };

    yield put(updateSlidesAnalyticsUpdateTimeAction(time));
    yield call(postSlidesAnalytics, slidesAnalytics._id, slideData);
}

export function* findPresentationByIdSaga(action: IFindPresentationByIdAction): unknown {
    const {id, credential, params} = action.payload;
    const {slideId, ...trimmedParams} = params;

    const state: AppState = yield select();
    const {runFile} = state.runFileReducer;

    try {
        const presentation: IPresentation = yield call(fetchPresentationById, id, credential?.password || null, trimmedParams);

        if (!presentation.found) {
            yield put(updatePresentationStatusAction({found: false, id}));
            return;
        }

        yield put(updatePresentationAction(presentation));

        yield call(getRunFile, presentation, trimmedParams, undefined, undefined, undefined, undefined, credential?.identification);

        if (presentation.dataCollection && runFile._id) yield put(fetchSlidesAnalyticsAction(runFile._id));

        yield call(loadSlidesSaga, presentation, credential?.password || null);

        if (slideId) {
            const visibleSlideList = yield select(visibleSlideSelector);
            const slideIndex = visibleSlideList.findIndex((slide: ISlide) => slide._id === slideId);
            if (slideIndex !== -1) {
                yield put(updateCurrentSlide(slideIndex));
                yield call(addSlideToRunFile, slideIndex);
            }
            const url = new URL(window.location.href);
            url.searchParams.delete('slideId');
            window.history.replaceState(null, '', url.toString());
        }

        // yield put(fetchPresentationPreviewsAction());
        yield call(buildSequenceQueueForCurrentSlideSaga);
        yield put(updatePresentationRunning(true));
    } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 403) {
            yield put(setPasswordErrorAction(e.response?.data.message || 'validations.credentials.incorrectPassword'));
        } else {
            toast.warning(t('message.error.tryAgain'), {toastId: 'tryAgain'});
        }
    }
}

export function* getPresentationInRoomSaga(action: IGetPresentationInRoomAction): unknown {
    const {params, credential, hash, presenterCode} = action.payload;

    const state: AppState = yield select();
    const {room, roomRunFile} = state.roomRunFileReducer;
    let presentation: IPresentation = {} as IPresentation;

    if (!room.isAllowed) return;

    presentation = {...(room.presentation || ({} as IPresentation)), found: room.isAllowed};

    yield put(updatePresentationAction(presentation));

    yield call(
        getRunFile,
        presentation,
        params,
        room.isPresenter,
        roomRunFile.runFileId,
        room.roomId,
        room.roomRunFileId,
        credential?.identification
    );

    yield call(loadSlidesSaga, presentation, credential?.password || '', room.roomId, hash, presenterCode);

    // yield put(fetchPresentationPreviewsAction());
    yield call(buildSequenceQueueForCurrentSlideSaga);
    yield put(updatePresentationRunning(true));
}

export function* handleElementsClickedSaga(action: IHandleElementsClicked): unknown {
    const {clearBeforeClick, clickedElementIds} = action.payload;

    if (clearBeforeClick) {
        yield put(clearElementClickedAction());
    }

    const state: AppState = yield select();
    const currentlyClickedElements = state.presentationReducer.clickedElementIds;
    const newClickedElementIds = getSymmetricalDifference(currentlyClickedElements, clickedElementIds);

    yield put(setElementsClickedAction(newClickedElementIds));
}

export function* toggleDrawEnabledSaga(): unknown {
    const state: AppState = yield select();
    const {drawEnabled} = state.presentationReducer;
    yield put(updateDrawEnabledAction(!drawEnabled));
}

export function* endPresentationSaga(action: IEndPresentationAction): unknown {
    window.dispatchEvent(new Event(PRESENTATION_ENDED_EVENT));

    const navigate = action.payload;
    const state: AppState = yield select();
    const {currentSlideIndex, dataCollection} = state.presentationReducer;
    const {roomRunFileId} = state.roomRunFileReducer.room;
    const {room} = state.roomRunFileReducer;

    const slug = getSlugFromUrl();

    if (dataCollection) yield call(addTimestampsToSlidesAnalytics, currentSlideIndex);

    yield call(navigate as (a: string) => void, '/');
    if (room.isPresenter) {
        if (socket) {
            socket.emit('closeRoom', {roomId: room.roomRunFileId});
        }
    }

    const presId = state.presentationReducer.loadedPresentation?._id;
    if (presId) localStorage.removeItem(getRunFileStorageKey(presId, roomRunFileId, slug));
    localStorage.removeItem('loadedPresentation');
    yield put(resetPresentationReducerAction());
    yield put(resetRunfileAction());
    yield put(closeConfig());
    yield call(closeModalAction);
    yield put(setFullscreenAction(false));
}

export function* startUploadedPresentationSaga(action: IStartUploadedPresentation): unknown {
    const presentation = action.payload;
    const updatedPresentation = convertPresentationToLatestVersion(presentation);
    yield put(updatePresentationAction({...updatedPresentation, found: true}));

    const hasSlides = presentation.slides && presentation.slides.length > 0;
    yield put(updatePresentationHasSlidesAction(hasSlides));

    yield call(getRunFile, presentation);
    yield call(refreshPresentationSaga, updatedPresentation);

    yield call(buildSequenceQueueForCurrentSlideSaga);
}

export function* scrollLeftSaga(): unknown {
    const state: AppState = yield select();
    const {currentSlideIndex} = state.presentationReducer;

    yield put(jumpToSlideAction(currentSlideIndex - 1));
}

export function* scrollRightSaga(): unknown {
    const state: AppState = yield select();
    const {currentSlideIndex, sequenceQueue} = state.presentationReducer;

    if (sequenceQueue.length > 0) {
        yield put(advanceSequenceStepAction());
        return;
    }

    yield put(jumpToSlideAction(currentSlideIndex + 1));
}

export function* jumpToSlideWithIdSaga(action: IJumpToSlideWithId): unknown {
    const {slideId, overrideBlockers} = action.payload;
    const visibleSlideList = yield select(visibleSlideSelector);
    const newSlideIndex = visibleSlideList.findIndex((slide: ISlide) => slide._id === slideId);

    if (newSlideIndex !== -1) {
        yield put(jumpToSlideAction(newSlideIndex, overrideBlockers));
    }
}

export function* jumpToSlideSaga(action: IJumpToSlide): unknown {
    const state: AppState = yield select();
    const visibleSlideList = yield select(visibleSlideSelector);
    const {lastShowedSlide, currentSlideIndex, dataCollection, loadedPresentation} = state.presentationReducer;
    const appliedRules = loadedPresentation?.appliedRules || [];
    const {runFile} = state.runFileReducer;
    const {room, roomRunFile} = state.roomRunFileReducer;
    const currentSlide = visibleSlideList[currentSlideIndex];
    const {index} = action.payload;

    if (!loadedPresentation) return;

    if (index < 0 || index === currentSlideIndex) return;

    if (dataCollection) yield call(addTimestampsToSlidesAnalytics, currentSlideIndex);

    if (index >= visibleSlideList.length) {
        // TODO: do we need to update analytics here?
        yield put(openEndPresentationAction());
        return;
    }

    const isRoom = !!room.roomId && !!roomRunFile._id;
    const isBlockedInRoom = isRoom && checkIfBlockedInRoom(room, roomRunFile, index);

    if (
        action.payload.overrideBlockers ||
        !checkShouldBlock(appliedRules, currentSlide, runFile, room, roomRunFile, index, lastShowedSlide, currentSlideIndex)
    ) {
        yield call(addSlideToRunFile, index);

        yield put(resetSequenceQueueAction());
        yield call(buildSequenceQueueForSlideSaga, visibleSlideList[index]);

        yield put(updateCurrentSlide(index));
        if (room.isPresenter) yield put(updateCurrentOwnerSlideAction(index));
        if (lastShowedSlide < index) yield put(updateLastShowedSlide(index));
    } else {
        const toastStr = isBlockedInRoom ? 'nextSlideBlockedByRoomOwner' : 'nextSlideBlocked';
        toast.error(t(`playerPage.${toastStr}`), {toastId: toastStr});
    }
}

function* buildSequenceQueueForSlideSaga(slide: ISlide | undefined): unknown {
    if (!slide?.elements) return;
    const queue: SequenceQueueEntry[] = [];
    slide.elements.forEach((el) => {
        if (el.elementAnimations?.sequence) {
            queue.push({elementId: el.id});
        }
    });
    if (queue.length > 0) yield put(setSequenceQueueAction(queue));
}

function* buildSequenceQueueForCurrentSlideSaga(): unknown {
    const state: AppState = yield select();
    const visibleSlides: ISlide[] = yield select(visibleSlideSelector);
    const slide = visibleSlides?.[state.presentationReducer.currentSlideIndex];
    yield call(buildSequenceQueueForSlideSaga, slide);
}

export function* watchPresentationSaga(): Generator<ForkEffect<never>, void> {
    yield takeLatest(SCROLL_LEFT, scrollLeftSaga);
    yield takeLatest(SCROLL_RIGHT, scrollRightSaga);
    yield takeLatest(JUMP_TO_SLIDE, jumpToSlideSaga);
    yield takeLatest(JUMP_TO_SLIDE_WITH_ID, jumpToSlideWithIdSaga);
    yield takeLatest(FIND_PRESENTATION_BY_ID, findPresentationByIdSaga);
    yield takeLatest(GET_PRESENTATION_IN_ROOM, getPresentationInRoomSaga);
    yield takeLatest(START_UPLOADED_PRESENTATION, startUploadedPresentationSaga);
    yield takeLatest(TOGGLE_DRAW_ENABLED, toggleDrawEnabledSaga);
    yield takeLatest(HANDLE_ELEMENTS_CLICKED, handleElementsClickedSaga);
    yield takeLatest(END_PRESENTATION, endPresentationSaga);
}
